import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import { API_URL } from '../lib/api'
import { useUTM } from '../hooks/useUTM'

interface BlogPostData {
  id: string
  slug: string
  title: string
  content: string
  meta_description: string
  published_at: string
  reading_time_minutes: number
}

export default function BlogPost() {
  useUTM()
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<BlogPostData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    fetch(`${API_URL}/api/blog/${slug}`)
      .then(res => {
        if (!res.ok) {
          setNotFound(true)
          setLoading(false)
          return null
        }
        return res.json()
      })
      .then(data => {
        if (data) {
          setPost(data.data || data)
        }
        setLoading(false)
      })
      .catch(() => {
        setNotFound(true)
        setLoading(false)
      })
  }, [slug])

  if (loading) {
    return (
      <section className="section">
        <div className="container">
          <p style={{ textAlign: 'center', padding: '3rem 0', color: '#666' }}>Loading...</p>
        </div>
      </section>
    )
  }

  if (notFound || !post) {
    return (
      <>
        <section className="page-hero page-hero--short">
          <div className="page-hero-overlay" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1>Post Not Found</h1>
              <p>The article you are looking for does not exist.</p>
            </motion.div>
          </div>
        </section>
        <section className="section">
          <div className="container" style={{ textAlign: 'center' }}>
            <Link to="/blog" className="btn btn-primary">
              Back to Blog
            </Link>
          </div>
        </section>
      </>
    )
  }

  return (
    <>
      <section className="page-hero page-hero--short">
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Blog</span>
            <h1>{post.title}</h1>
            <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              {formatDate(post.published_at)} &middot; {post.reading_time_minutes || 5} min read
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link
                to="/blog"
                style={{
                  display: 'inline-block',
                  marginBottom: '2rem',
                  color: '#dc2626',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              >
                &larr; Back to Blog
              </Link>

              <div
                className="blog-content"
                style={{
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                  color: '#333',
                }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>

            {/* CTA Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{
                marginTop: '4rem',
                padding: '3rem 2rem',
                background: '#fafafa',
                border: '1px solid #e5e7eb',
                borderRadius: '5px',
                textAlign: 'center',
              }}
            >
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 500,
                color: '#0f172a',
                marginBottom: '0.75rem',
              }}>
                Get the free 7-Day Carnivore Reset Guide
              </h3>
              <p style={{
                color: '#555',
                marginBottom: '1.5rem',
                maxWidth: '480px',
                margin: '0 auto 1.5rem',
              }}>
                Everything you need to start your carnivore journey, delivered to your inbox.
              </p>
              <Link to="/free" className="btn btn-primary">
                Get the Free Guide
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}
